/**
 * Cart offers: free shipping and a free gift (snippets/nyl-cart-offers.liquid).
 *
 * Draws the bar wherever the snippet lands, when the page loads and each time
 * it is drawn again: the Added to bag panel after every add or removal, and
 * Horizon's cart after every change. The amounts come in the store's currency
 * and are shown in the visitor's own, at the rate Shopify Markets uses for
 * prices (Shopify.currency.rate), unless the market has an exact figure of
 * its own. They are formatted from a sample of the page's own money filter,
 * so they read exactly as prices do.
 *
 * The same amount fills any line of copy written with [amount], the product
 * page's shipping note and the trust bar (snippets/nyl-ship-line.liquid), so
 * no two places on a page can promise different figures.
 *
 * Free shipping outside the store's own country is Global-e's while their app
 * is installed, so there the amount is theirs, read from the banner their
 * script adds and remembered for the visit so later pages need not wait for
 * it again. Where their app is installed and gives a country nothing, that
 * country shows no free shipping bar, because the bar must not promise what
 * their checkout will not give. Where their app has gone, the page's own
 * amount is all there is and the bar shows that rather than disappearing:
 * Shopify loads app scripts on window load, so nothing of theirs on the page
 * a while after that means there is nothing left to wait for.
 *
 * The free gift goes in the bag once the bag reaches its amount and comes out
 * if it drops below, marked with a hidden _nyl_gift property. It is only kept
 * if Shopify prices it at zero, which is the team's automatic discount: if it
 * comes in at a price, it goes straight back out and is not tried again in
 * that visit. A shopper who removes it is not given it again in that visit
 * either.
 */
(() => {
  const root = () => window.Shopify?.routes?.root || '/';
  const DECLINED = 'nyl:gift:declined';
  const UNPRICED = 'nyl:gift:unpriced';
  const LAST = 'nyl:offers:last';
  // Global-e's amount for this country and currency, and the note that their
  // app is no longer on this store. Both are for the visit only, so the first
  // page of the next one asks again.
  const THEIRS = 'nyl:ship:theirs';
  const GONE = 'nyl:ship:gone';

  const session = {
    get(key) {
      try {
        return window.sessionStorage.getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.sessionStorage.setItem(key, value);
      } catch (error) {
        // Private browsing. The rule then holds for this page only.
      }
    },
  };

  // The gifts a shopper took out, and the ones nothing priced at zero, each
  // a list of variant ids for the visit, since a milestone can be a gift too.
  const listed = (key, variant) => (session.get(key) || '').split(',').includes(String(variant));
  const list = (key, variant) => {
    const ids = new Set((session.get(key) || '').split(',').filter(Boolean));
    ids.add(String(variant));
    session.set(key, [...ids].join(','));
  };

  const dispatch = (name, detail) => {
    document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
  };

  // The page prints 1,234,567 through its own money filter, "Rs.1,234,567"
  // say, and whole amounts are written in the same shape: the same symbol,
  // the same side, the same separator.
  const formatterFrom = (sample) => {
    // (?:567) keeps the back reference to \1 alone: written \1567 it reads as
    // an old octal escape and nothing ever matches.
    const match = /1(\D?)234\1(?:567)/.exec(sample || '');
    if (!match) return (major) => String(major);
    const [whole, separator] = match;
    const before = sample.slice(0, match.index);
    const after = sample.slice(match.index + whole.length);
    return (major) => `${before}${String(major).replace(/\B(?=(\d{3})+(?!\d))/g, separator)}${after}`;
  };

  // Global-e's banner, which its script adds to every page outside the
  // store's own country when it has free shipping there.
  const GLOBAL_E = '#FreeShippingBanner, .ge-free-shipping-msg';

  // Global-e's free shipping amount for this visitor's country, read from
  // that banner: "Free shipping to United States on all orders above
  // $150.00" gives 15000, in hundredths of the visitor's currency. The
  // largest figure in the line, with a point or comma before any pence.
  // Nothing until the banner is on the page, and nothing where Global-e
  // offers no free shipping, so the bar never promises what their checkout
  // does not give.
  const globalEAmount = () => {
    const text = document.querySelector(GLOBAL_E)?.textContent || '';
    const figures = (text.match(/\d(?:[\d.,'\s]*\d)?/g) || []).map((raw) => {
      const digits = raw.replace(/[\s']/g, '');
      const point = digits.search(/[.,]\d{1,2}$/);
      const whole = (point > -1 ? digits.slice(0, point) : digits).replace(/[.,]/g, '');
      const pence = point > -1 ? digits.slice(point + 1).padEnd(2, '0') : '0';
      return Number(whole) * 100 + Number(pence);
    });
    const most = Math.max(0, ...figures.filter(Number.isFinite));
    return most > 0 ? most : 0;
  };

  // Anything of Global-e's on the page, which says their app is still
  // installed even before its banner has been drawn or where a country is
  // given no free shipping at all.
  const GLOBAL_E_APP = '.ge-free-shipping-container, script[src*="global-e"]';
  const globalEHere = () =>
    Boolean(
      window.GlobalE ||
        window.GlobaleApp ||
        document.querySelector(GLOBAL_E) ||
        document.querySelector(GLOBAL_E_APP)
    );

  // Shopify loads an app's scripts on window load, and Global-e's chain adds
  // its banner about a second after that. Until this has passed, a bar that
  // follows them waits rather than showing an amount of its own that their
  // checkout would not give.
  const GRACE = 6000;
  let waited = false;

  // In the visitor's currency, in hundredths as Shopify counts money.
  const amountFor = (local, base, rate) => {
    if (local) return Number(local) || 0;
    const store = Number(base) || 0;
    return store > 0 ? Math.round(store * rate) : 0;
  };

  // Where the market is Global-e's, this country and currency, so an amount
  // of theirs is only reused where it still applies.
  const here = () => `${window.Shopify?.country || ''} ${window.Shopify?.currency?.active || ''}`;

  // The free shipping amount to show, in the visitor's currency.
  const shipAmount = (el, rate) => {
    const own = amountFor(el.dataset.shipLocal, el.dataset.shipBase, rate);
    if (el.dataset.shipSource !== 'global-e') return own;

    // Their banner, when their script has drawn it.
    const theirs = globalEAmount();
    if (theirs > 0) {
      session.set(THEIRS, `${here()} ${theirs}`);
      return theirs;
    }

    // Or what it said on an earlier page of this visit.
    const [country, currency, seen = ''] = (session.get(THEIRS) || '').split(' ');
    const before = `${country} ${currency}` === here() ? Number(seen) || 0 : 0;
    if (before > 0) return before;

    // Their app is here and has given this country nothing, so neither does
    // the bar.
    if (globalEHere()) return 0;

    // Their app has gone, so the page's own amount is what there is. Noted
    // for the visit, so only the first page of it waits.
    if (session.get(GONE) === '1') return own;
    if (!waited) return 0;
    session.set(GONE, '1');
    return own;
  };

  const post = (path, body) =>
    fetch(`${root()}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((response) => {
      if (!response.ok) throw new Error(String(response.status));
      return response.json();
    });

  let busy = false;

  const giftIn = async (variant) => {
    busy = true;
    try {
      await post('cart/add.js', {
        items: [{ id: Number(variant), quantity: 1, properties: { _nyl_gift: 'Free gift' } }],
      });
      const cart = await fetch(`${root()}cart.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      }).then((response) => response.json());
      const line = cart.items.find(
        (item) => item.properties?._nyl_gift && String(item.variant_id) === String(variant)
      );
      if (!line) return;

      if (line.final_line_price > 0) {
        // Nothing prices it at zero, so it would be charged. Out it goes, and
        // every bar is drawn again without it, so none goes on promising it.
        list(UNPRICED, variant);
        await post('cart/change.js', { id: line.key, quantity: 0 });
        console.warn(
          'Now You Live: the free gift came into the bag at a price, so it was taken out again. It needs its automatic discount in Shopify.'
        );
        busy = false;
        drawIn(document);
        return;
      }

      dispatch('cart:refresh');
      dispatch('nyl:cart:added', { handles: [] });
      dispatch('nyl:bag:refresh', { key: line.key });
    } catch (error) {
      // The bag stays as it was.
    } finally {
      busy = false;
    }
  };

  const giftOut = async (key) => {
    busy = true;
    try {
      await post('cart/change.js', { id: key, quantity: 0 });
      dispatch('cart:refresh');
      dispatch('nyl:cart:removed', { keys: [key] });
      dispatch('nyl:bag:refresh', { removed: key });
    } catch (error) {
      // The bag stays as it was.
    } finally {
      busy = false;
    }
  };

  // The last total a bar showed, with its currency. Kept for the visit, or
  // for this page where the browser keeps nothing.
  let seenHere = null;
  const lastSeen = {
    get: () => session.get(LAST) ?? seenHere,
    set: (value) => {
      seenHere = value;
      session.set(LAST, value);
    },
  };

  // A fill only moves once its bar can be seen: the panel is often drawn
  // before its dialog opens.
  const reveal =
    'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            reveal.unobserve(entry.target);
            run(entry.target);
          });
        })
      : null;

  const run = (el) => {
    const fill = el.querySelector('[data-nyl-offers-fill]');
    if (!fill || !el.dataset.fillTo) return;
    fill.style.width = el.dataset.fillTo;
    if (el.dataset.arrived === 'true') {
      el.classList.remove('is-arriving');
      // Restarts the arrival if the same bar arrives twice.
      void el.offsetWidth;
      el.classList.add('is-arriving');
    }
  };

  // The line of copy, with the amount in bold and the reward's name in
  // place of [reward] and [gift]. A free product to pick is followed by a
  // link to its collection. Built as text, never markup.
  const write = (label, lines, giftName) => {
    label.replaceChildren();
    lines.forEach(({ text, amount, reward = '', links = [] }, index) => {
      if (index) label.append('\n');
      (text || '').trim().split('[amount]').forEach((part, at) => {
        if (at) {
          const strong = document.createElement('strong');
          strong.textContent = amount;
          label.append(strong);
        }
        label.append(part.split('[gift]').join(giftName).split('[reward]').join(reward));
      });
      links.forEach(({ href, text: words }) => {
        if (!href || !words) return;
        const anchor = document.createElement('a');
        anchor.className = 'nyl-offers__link';
        anchor.href = href;
        anchor.textContent = words;
        label.append(' \u00b7 ', anchor);
      });
    });
  };

  // A reward's icon, from the ones the page printed for this bar.
  const iconFor = (el, name, size) => {
    const attribute = size === 'stop' ? 'data-nyl-offers-stop-icon' : 'data-nyl-offers-icon';
    const template = [...el.querySelectorAll(`template[${attribute}]`)].find(
      (item) => item.getAttribute(attribute) === name
    );
    return template ? template.content.cloneNode(true) : null;
  };

  // One row to each line of copy, with that reward's own icon beside it and
  // a tick once it is reached. The first row is the page's own; the others
  // are copies of it, made and dropped as lines come and go. With nothing to
  // say, the row stands empty and hidden.
  const writeRows = (el, lines, giftName) => {
    const label = el.querySelector('[data-nyl-offers-text]');
    const first = label?.closest('.nyl-offers__head');
    if (!label || !first) return;
    el.querySelectorAll('[data-nyl-offers-extra]').forEach((row) => row.remove());
    first.hidden = !lines.length;
    if (!lines.length) {
      label.replaceChildren();
      return;
    }
    let after = first;
    lines.forEach((line, index) => {
      let row = first;
      let text = label;
      if (index) {
        row = first.cloneNode(true);
        row.setAttribute('data-nyl-offers-extra', '');
        text = row.querySelector('[data-nyl-offers-text]');
        text.removeAttribute('data-nyl-offers-text');
        after.after(row);
        after = row;
      }
      const glyph = row.querySelector('[data-nyl-offers-glyph]');
      if (glyph && glyph.dataset.icon !== line.icon) {
        glyph.replaceChildren();
        const icon = iconFor(el, line.icon, 'row');
        if (icon) glyph.append(icon);
        glyph.dataset.icon = line.icon;
      }
      glyph?.classList.toggle('nyl-offers__glyph--truck', line.icon === 'truck');
      row.classList.toggle('is-reached', Boolean(line.reached));
      write(text, [line], giftName);
    });
  };

  // The milestones the page printed for this bar (Theme settings, Cart
  // milestones), as JSON beside it.
  const tiersOf = (el) => {
    const data = el.querySelector('script[data-nyl-offers-tiers]');
    if (!data) return [];
    try {
      const tiers = JSON.parse(data.textContent);
      return Array.isArray(tiers) ? tiers : [];
    } catch (error) {
      return [];
    }
  };

  // A gift follows the bag, whether or not the bar shows here: in once its
  // amount is reached, out if the bag drops below it, and out as well if it
  // is still in at a price because taking it out before failed. One change
  // at a time; the bag's redraw brings the next.
  const follow = (el, offers) => {
    if (busy) return;
    // A £0 product in the bag that no offer gave, first of all.
    const [stray] = (el.dataset.stray || '').split(',').filter(Boolean);
    if (stray) {
      giftOut(stray);
      return;
    }
    const auto = el.dataset.giftAuto === 'true';
    for (const offer of offers) {
      if (!offer.variant || !(offer.at > 0)) continue;
      const reached = offer.have >= offer.at;
      if (offer.key && (!reached || offer.unpriced)) {
        giftOut(offer.key);
        return;
      }
      if (reached && !offer.key && auto && !offer.gone) {
        giftIn(offer.variant);
        return;
      }
    }
  };

  const draw = (el) => {
    const rate = Number(window.Shopify?.currency?.rate) || 1;
    const total = Number(el.dataset.total) || 0;
    // The bag before discounts on the order as a whole, which is what the
    // rewards' own minimums count, so a milestone's money off, once applied,
    // does not take the bag back under it. Free shipping counts the total
    // after every discount, as checkout does.
    const subtotal = Number(el.dataset.subtotal) || total;
    const money = formatterFrom(el.dataset.money);
    const giftName = el.dataset.giftName || '';

    const offers = [];
    if (el.dataset.shipBase) {
      offers.push({
        ship: true,
        at: shipAmount(el, rate),
        progress: el.dataset.shipProgress,
        done: el.dataset.shipDone,
        label: el.dataset.shipLabel,
        icon: el.dataset.shipIcon || 'truck',
      });
    }
    if (el.dataset.giftVariant) {
      offers.push({
        at: amountFor(el.dataset.giftLocal, el.dataset.giftBase, rate),
        progress: el.dataset.giftProgress,
        done: el.dataset.giftDone,
        label: el.dataset.giftLabel,
        icon: el.dataset.giftIcon || 'box',
        variant: el.dataset.giftVariant,
        key: el.dataset.giftKey,
      });
    }
    tiersOf(el).forEach((tier) => {
      offers.push({
        at: amountFor('', tier.base, rate),
        progress: tier.progress,
        done: tier.done,
        label: tier.label,
        reward: tier.label,
        icon: tier.icon || 'box',
        variant: tier.variant || '',
        key: tier.key || '',
        link: tier.link || '',
      });
    });
    // A gift that came in at a price, or that the shopper took out, is not
    // put in again this visit, so the bar stops offering it as well.
    offers.forEach((offer) => {
      offer.have = offer.ship ? total : subtotal;
      offer.reward = offer.reward || (offer.label || '').split('[gift]').join(giftName);
      offer.unpriced = Boolean(offer.variant) && listed(UNPRICED, offer.variant);
      offer.gone = offer.unpriced || (Boolean(offer.variant) && listed(DECLINED, offer.variant));
    });
    const live = offers.filter((offer) => offer.at > 0 && !offer.gone).sort((a, b) => a.at - b.at);
    if (!live.length) {
      if (!el.hidden) el.hidden = true;
      follow(el, offers);
      return;
    }

    // One line only: the next amount still to reach, or, with everything
    // reached, what the bag has earned, the rewards named together. A free
    // product still to pick adds its link to the line either way. A gift
    // counts as earned once it is really in the bag, a moment after its
    // amount.
    const next = live.find((offer) => offer.have < offer.at);
    const earned = live.filter((offer) => offer.have >= offer.at && (!offer.variant || offer.key));
    const links = earned
      .filter((offer) => offer.link)
      .map((offer) => ({ href: offer.link, text: (el.dataset.tierLink || '').split('[reward]').join(offer.reward) }));
    let line = null;
    if (next) {
      line = {
        text: next.progress,
        amount: money(Math.ceil((next.at - next.have) / 100)),
        icon: next.icon,
        reward: next.reward,
      };
    } else if (earned.length === 1) {
      const [only] = earned;
      line = { text: only.done, amount: '', icon: only.icon, reward: only.reward, reached: true };
    } else if (earned.length > 1) {
      const names = earned.map((offer) => offer.reward).filter(Boolean);
      let rewards = names.join(', ');
      try {
        rewards = new Intl.ListFormat('en-GB', { type: 'conjunction' }).format(names);
      } catch (error) {
        // Older browsers: a plain list.
      }
      line = {
        text: (el.dataset.allDone || "You've unlocked [rewards]").split('[rewards]').join(rewards),
        amount: '',
        icon: earned[earned.length - 1].icon,
        reached: true,
      };
    }
    if (line) line.links = links;
    const lines = line && line.text?.trim() ? [line] : [];

    const top = live[live.length - 1].at;
    const measure = live.some((offer) => !offer.ship) ? subtotal : total;
    const share = Math.min(1, measure / top);
    const fill = el.querySelector('[data-nyl-offers-fill]');
    const track = el.querySelector('[data-nyl-offers-track]');
    writeRows(el, lines, giftName);
    // More than one reward: each is a stop on the track, a dot that fills
    // once reached, with the reward's label, amount or icon beneath it.
    const stops = el.querySelector('[data-nyl-offers-stops]');
    if (track) {
      track.querySelectorAll('.nyl-offers__mark').forEach((mark) => mark.remove());
      if (live.length > 1) {
        live.forEach((offer) => {
          const mark = document.createElement('span');
          mark.className = `nyl-offers__mark${offer.have >= offer.at ? ' is-reached' : ''}`;
          mark.style.left = `${(offer.at / top) * 100}%`;
          track.append(mark);
        });
      }
    }
    if (stops) {
      stops.querySelectorAll('.nyl-offers__stop').forEach((stop) => stop.remove());
      if (live.length > 1) {
        live.forEach((offer, index) => {
          const share = (offer.at / top) * 100;
          const stop = document.createElement('span');
          stop.className = `nyl-offers__stop${offer.have >= offer.at ? ' is-reached' : ''}`;
          if (index === live.length - 1) stop.classList.add('is-last');
          stop.style.left = `${share}%`;
          // What the team chose to show beneath each stop (Theme settings,
          // Cart offers): its label, "Free Shipping" say, or its amount,
          // either with its icon. A reward with no label shows its amount.
          const style = el.dataset.stopStyle || 'label';
          if (style.startsWith('icon_')) {
            const icon = iconFor(el, offer.icon, 'stop');
            if (icon) stop.append(icon);
          }
          const word = style.endsWith('label') ? (offer.reward || '').trim() : '';
          const text = document.createElement('span');
          text.className = 'nyl-offers__stop-text';
          text.textContent = word || money(Math.round(offer.at / 100));
          stop.append(text);
          stop.dataset.at = String(share);
          stops.append(stop);
        });
      }
      stops.hidden = live.length < 2;
      settle(stops);
      settling?.observe(stops);
    }
    el.classList.toggle('is-done', !next);
    // Only when it changes: the observer below watches this attribute.
    const hide = el.dataset.show !== 'true';
    if (el.hidden !== hide) el.hidden = hide;

    // The fill runs from the last total any bar showed in this visit, in
    // this currency, to the new one, once the bar is in view, so every
    // change reads as progress, from one page to the next as well. A visit's
    // first bar starts from an empty bag. Crossing an amount on the way plays
    // the arrival: a tick on the icon and a sheen along the fill.
    const to = `${share * 100}%`;
    if (fill && !el.hidden && el.dataset.fillTo !== to) {
      const active = window.Shopify?.currency?.active || '';
      const [currency, stored = ''] = (lastSeen.get() || '').split(' ');
      const before = currency === active && stored !== '' ? Number(stored) || 0 : 0;
      lastSeen.set(`${active} ${measure}`);
      el.dataset.arrived = String(live.some((offer) => before < offer.at && offer.have >= offer.at));
      el.dataset.fillTo = to;
      fill.style.transition = 'none';
      fill.style.width = `${Math.min(1, before / top) * 100}%`;
      void fill.offsetWidth;
      fill.style.transition = '';
      if (reveal) reveal.observe(el);
      else run(el);
    } else if (fill && el.hidden) {
      fill.style.width = to;
    }

    follow(el, offers);
  };

  // Amounts close together, £90 and £100 say, would print over each other
  // beneath their stops. Working back from the last, which sits at the
  // track's end, a label that would run into the one after it moves left of
  // its stop until it clears it. One with no room even then keeps its icon
  // and drops its amount, and the first never starts before the track does.
  // Measured, since the width of an amount depends on its currency, and done
  // again whenever the row changes size: a panel is often drawn closed.
  const GAP = 10;
  const settle = (stops) => {
    const labels = [...stops.querySelectorAll('.nyl-offers__stop')];
    const box = stops.getBoundingClientRect();
    if (!labels.length || !box.width) return;
    labels.forEach((label) => {
      label.style.left = `${label.dataset.at}%`;
      label.style.transform = '';
      label.classList.remove('is-bare');
    });
    for (let index = labels.length - 2; index >= 0; index -= 1) {
      const label = labels[index];
      const limit = labels[index + 1].getBoundingClientRect().left - GAP;
      if (label.getBoundingClientRect().right <= limit) continue;
      label.style.left = `${limit - box.left}px`;
      label.style.transform = 'translateX(-100%)';
      if (label.getBoundingClientRect().left < box.left && label.querySelector('svg')) label.classList.add('is-bare');
    }
    const first = labels[0];
    if (first.getBoundingClientRect().left < box.left) {
      first.style.left = '0px';
      first.style.transform = 'none';
    }
  };

  const settling =
    'ResizeObserver' in window
      ? new ResizeObserver((entries) => {
          entries.forEach(({ target }) => {
            if (target.isConnected) settle(target);
            else settling.unobserve(target);
          });
        })
      : null;

  // A line of copy with [amount] in it, such as the product page's shipping
  // note and the trust bar's free shipping (snippets/nyl-ship-line.liquid):
  // this market's free shipping amount filled in, the same one the bar
  // shows. While Global-e may still answer, the line keeps its room unseen
  // (data-nyl-ship-waiting), so nothing below it moves when their figure
  // lands; where there is no amount to give, it goes altogether.
  const drawLine = (el) => {
    const rate = Number(window.Shopify?.currency?.rate) || 1;
    const at = shipAmount(el, rate);
    const wrap = el.closest('[data-nyl-ship-wrap]') || el;
    if (at <= 0) {
      const waiting = el.dataset.shipSource === 'global-e' && !waited && session.get(GONE) !== '1';
      if (waiting) return;
      wrap.removeAttribute('data-nyl-ship-waiting');
      if (!wrap.hidden) wrap.hidden = true;
      return;
    }
    const money = formatterFrom(el.dataset.money);
    const text = (el.dataset.text || '').split('[amount]').join(money(Math.round(at / 100)));
    if (el.textContent !== text) el.textContent = text;
    wrap.removeAttribute('data-nyl-ship-waiting');
    if (wrap.hidden) wrap.hidden = false;
  };

  const drawIn = (scope) => {
    if (scope.matches?.('[data-nyl-offers]')) draw(scope);
    scope.querySelectorAll?.('[data-nyl-offers]').forEach(draw);
    if (scope.matches?.('[data-nyl-ship-line]')) drawLine(scope);
    scope.querySelectorAll?.('[data-nyl-ship-line]').forEach(drawLine);
  };

  // A shopper who takes the gift out has said no to it for this visit.
  document.addEventListener(
    'click',
    (event) => {
      const remove = event.target.closest?.('[data-nyl-qa-remove], .cart-items__remove');
      const line = remove?.closest('[data-nyl-gift]');
      if (line?.dataset.nylGift) list(DECLINED, line.dataset.nylGift);
    },
    true
  );

  // The panel and Horizon's cart both draw the snippet again after a change.
  // Horizon's cart morphs it in place, back to the server's markup: hidden,
  // empty and with no fill. That happens even when the total has not moved,
  // an unknown discount code say, so the bar's hidden attribute coming back
  // counts as a redraw as much as a changed total does.
  //
  // Apps add nodes to the page in their hundreds, so each added node is only
  // checked once for anything the bars care about, and each batch of
  // changes is drawn once. Page speed tests put this script's own work at
  // 124 to 198ms a page before (2026-09-25). The drawing does not wait for
  // a frame: a tab in the background runs none, and its bar would stay
  // blank until it was looked at.
  const WATCHED = `[data-nyl-offers], [data-nyl-ship-line], ${GLOBAL_E}`;
  const queued = new Set();
  let redrawAll = false;

  const flush = () => {
    if (redrawAll) {
      redrawAll = false;
      queued.clear();
      drawIn(document);
      return;
    }
    queued.forEach((node) => {
      if (node.isConnected) drawIn(node);
    });
    queued.clear();
  };

  const queue = (node) => {
    if (node === document) redrawAll = true;
    else queued.add(node);
  };

  new MutationObserver((records) => {
    records.forEach((record) => {
      if (record.type === 'attributes') {
        if (record.target.matches?.('[data-nyl-offers]')) queue(record.target);
        return;
      }
      record.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        const holds = node.matches(WATCHED) || (node.firstElementChild !== null && node.querySelector(WATCHED) !== null);
        if (!holds) return;
        // Global-e's banner landing gives every bar its free shipping amount.
        if (node.matches(GLOBAL_E) || node.querySelector(GLOBAL_E)) queue(document);
        else queue(node);
      });
    });
    if (redrawAll || queued.size) flush();
  }).observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-total', 'data-subtotal', 'data-gift-key', 'data-stray', 'hidden'],
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => drawIn(document), { once: true });
  } else {
    drawIn(document);
  }

  // Once Global-e's scripts have had their turn, every bar is drawn again, so
  // a bar that was waiting for them shows the page's own amount if their app
  // has gone. Their banner landing draws them again before that (below).
  const startWaiting = () => {
    window.setTimeout(() => {
      waited = true;
      drawIn(document);
    }, GRACE);
  };
  if (document.readyState === 'complete') startWaiting();
  else window.addEventListener('load', startWaiting, { once: true });
})();
